/**
 * Turns a time phrase in a question ("last month", "so far this year", "in the
 * last 30 days") into a concrete date range.
 *
 * Read-path only, like {@link ./fuzzyMatch}. Nothing here can affect a write, so
 * a misread phrase costs a wrong number in a sentence rather than a wrong edit.
 *
 * "Last month" deliberately means the *previous calendar month*, not the last
 * thirty days. A supplier asking what they earned last month is asking an
 * accounting question, and a trailing window would not reconcile against their
 * own books.
 *
 * Every answer that uses a period is expected to name {@link ResolvedPeriod.label}
 * back to the user. That is what makes the fallback safe: when a phrase is not
 * recognised the answer still says which window it actually covered, so an
 * unrecognised "since we opened" reads as "over the last 12 months" rather than
 * silently pretending to be all-time.
 */

import moment from 'moment';
import { containsFuzzyPhrase, tokenize } from './fuzzyMatch';

export interface ResolvedPeriod {
  id: string;
  /**
   * How the period is named back to the user. Phrased to slot straight after a
   * verb — "you were paid out $X {label}." — so answers do not have to special
   * case prepositions.
   */
  label: string;
  /** Inclusive start as `YYYY-MM-DD`. */
  startDate: string;
  /** Inclusive end as `YYYY-MM-DD`. */
  endDate: string;
  /** False when nothing in the message named a period and the default was used. */
  explicit: boolean;
}

const ISO_DAY = 'YYYY-MM-DD';

interface PeriodDefinition {
  id: string;
  label: string;
  /** Phrases that select this period. Matched longest-first. */
  phrases: string[];
  range: () => { start: moment.Moment; end: moment.Moment };
}

const PERIOD_DEFINITIONS: PeriodDefinition[] = [
  {
    id: 'today',
    label: 'today',
    phrases: ['today', 'so far today'],
    range: () => ({ start: moment().startOf('day'), end: moment().endOf('day') }),
  },
  {
    id: 'yesterday',
    label: 'yesterday',
    phrases: ['yesterday'],
    range: () => ({
      start: moment().subtract(1, 'day').startOf('day'),
      end: moment().subtract(1, 'day').endOf('day'),
    }),
  },
  {
    id: 'thisWeek',
    label: 'so far this week',
    phrases: ['this week', 'so far this week', 'week to date'],
    range: () => ({ start: moment().startOf('week'), end: moment().endOf('day') }),
  },
  {
    id: 'lastWeek',
    label: 'last week',
    phrases: ['last week', 'previous week'],
    range: () => ({
      start: moment().subtract(1, 'week').startOf('week'),
      end: moment().subtract(1, 'week').endOf('week'),
    }),
  },
  {
    id: 'thisMonth',
    label: 'so far this month',
    phrases: ['this month', 'so far this month', 'month to date'],
    range: () => ({ start: moment().startOf('month'), end: moment().endOf('day') }),
  },
  {
    id: 'lastMonth',
    label: 'last month',
    phrases: ['last month', 'previous month'],
    range: () => ({
      start: moment().subtract(1, 'month').startOf('month'),
      end: moment().subtract(1, 'month').endOf('month'),
    }),
  },
  {
    id: 'thisQuarter',
    label: 'so far this quarter',
    phrases: ['this quarter', 'quarter to date'],
    range: () => ({ start: moment().startOf('quarter'), end: moment().endOf('day') }),
  },
  {
    id: 'lastQuarter',
    label: 'last quarter',
    phrases: ['last quarter', 'previous quarter'],
    range: () => ({
      start: moment().subtract(1, 'quarter').startOf('quarter'),
      end: moment().subtract(1, 'quarter').endOf('quarter'),
    }),
  },
  {
    id: 'thisYear',
    label: 'so far this year',
    phrases: ['this year', 'year to date', 'ytd'],
    range: () => ({ start: moment().startOf('year'), end: moment().endOf('day') }),
  },
  {
    id: 'lastYear',
    label: 'last year',
    phrases: ['last year', 'previous year'],
    range: () => ({
      start: moment().subtract(1, 'year').startOf('year'),
      end: moment().subtract(1, 'year').endOf('year'),
    }),
  },
];

/**
 * Used when the question names no period.
 *
 * Matches the default range of the payouts report, so the assistant and the
 * report agree when a supplier cross-checks one against the other.
 */
const buildDefaultPeriod = (): ResolvedPeriod => ({
  id: 'trailingYear',
  label: 'over the last 12 months',
  startDate: moment().subtract(1, 'year').format(ISO_DAY),
  endDate: moment().endOf('day').format(ISO_DAY),
  explicit: false,
});

/** `last 30 days`, `past 7 days`, `previous 90 days`. */
const RELATIVE_DAYS = /\b(?:last|past|previous)\s+(\d{1,4})\s+days?\b/i;

const toPeriod = (definition: PeriodDefinition): ResolvedPeriod => {
  const { start, end } = definition.range();
  return {
    id: definition.id,
    label: definition.label,
    startDate: start.format(ISO_DAY),
    endDate: end.format(ISO_DAY),
    explicit: true,
  };
};

/**
 * Finds the period a message is asking about, or null when it names none.
 *
 * Phrases are matched typo-tolerantly and longest-first, so "so far this month"
 * is not shortened to "this month" if the two ever diverge.
 */
export const detectPeriod = (message: string): ResolvedPeriod | null => {
  // An explicit day count is unambiguous, so it wins over the named periods —
  // otherwise "the last 30 days" would be caught by nothing and fall through,
  // and a phrase like "last 7 days" must not be read as a calendar week.
  const relative = message.match(RELATIVE_DAYS);
  if (relative) {
    const days = Number(relative[1]);
    if (days > 0) {
      return {
        id: 'lastNDays',
        label: `in the last ${days} day${days === 1 ? '' : 's'}`,
        startDate: moment().subtract(days, 'days').startOf('day').format(ISO_DAY),
        endDate: moment().endOf('day').format(ISO_DAY),
        explicit: true,
      };
    }
  }

  const tokens = tokenize(message);

  let best: PeriodDefinition | null = null;
  let bestLength = 0;

  for (const definition of PERIOD_DEFINITIONS) {
    for (const phrase of definition.phrases) {
      if (phrase.length <= bestLength) continue;
      if (!containsFuzzyPhrase(tokens, tokenize(phrase))) continue;
      best = definition;
      bestLength = phrase.length;
    }
  }

  return best ? toPeriod(best) : null;
};

/** Same as {@link detectPeriod} but always yields a usable range. */
export const resolvePeriod = (message: string): ResolvedPeriod =>
  detectPeriod(message) ?? buildDefaultPeriod();

/**
 * Whether the window a period describes has already closed.
 *
 * Lives here rather than at the call site so that reading a period's raw dates
 * stays confined to this module and `apiDateRange` — those dates are ISO, and
 * handing them to an API that expects MM/DD/YYYY is a bug that tests with
 * mocked request modules cannot see.
 */
export const hasPeriodEnded = (period: Pick<ResolvedPeriod, 'endDate'> | null | undefined): boolean => {
  const end = period?.endDate;
  return Boolean(end) && moment(end).isBefore(moment().startOf('day'));
};
